import { createContext, ReactNode, useContext } from 'react';
import {
	DelimitedArrayParam,
	NumberParam,
	StringParam,
	useQueryParams,
	withDefault
} from 'use-query-params';
import { QueryKey } from '../lib/model';
import { ELECTION_OPTIONS } from '../lib/const';

export interface RouteContextInterface {
	selectedState: string | null;
	setSelectedState: (v: string | null) => void;
	election: string | null;
	setElection: (v: string | null) => void;
	congressTab: number | null;
	setCongressTab: (v: string | number | null) => void;
	donorTab: number;
	setDonorTab: (v: string | number | null) => void;
	seat: string[];
	setSeat: (v: string[]) => void;
	raceFilter: string[];
	setRaceFilter: (v: string[]) => void;
	sectionAnchor: string;
	setSectionAnchor: (v: string | null) => void;
	donorPage: string | null;
	setDonorPage: (v: string | null) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ROUTE_CONTEXT_DEFAULT: RouteContextInterface = {
	selectedState: 'az',
	setSelectedState: () => {},
	election: ELECTION_OPTIONS[1].id,
	setElection: () => {},
	congressTab: 0,
	setCongressTab: () => {},
	donorTab: 0,
	setDonorTab: () => {},
	seat: ['open'],
	setSeat: () => {},
	raceFilter: [],
	setRaceFilter: () => {},
	sectionAnchor: 'highlights',
	setSectionAnchor: () => {},
	donorPage: null,
	setDonorPage: () => {}
};

const RouteContext = createContext(ROUTE_CONTEXT_DEFAULT);

// eslint-disable-next-line react-refresh/only-export-components
export function useRoute() {
	return useContext<RouteContextInterface>(RouteContext);
}

export function RouteProvider({ children }: { children: ReactNode }) {
	const [query, setQuery] = useQueryParams({
		[QueryKey.State]: withDefault(
			StringParam,
			ROUTE_CONTEXT_DEFAULT.selectedState
		),
		[QueryKey.Election]: withDefault(
			StringParam,
			ROUTE_CONTEXT_DEFAULT.election
		),
		[QueryKey.Seat]: withDefault(
			DelimitedArrayParam,
			ROUTE_CONTEXT_DEFAULT.seat
		),
		[QueryKey.Congress]: withDefault(
			NumberParam,
			ROUTE_CONTEXT_DEFAULT.congressTab
		),
		[QueryKey.Donor]: withDefault(NumberParam, ROUTE_CONTEXT_DEFAULT.donorTab),
		[QueryKey.Race]: withDefault(
			DelimitedArrayParam,
			ROUTE_CONTEXT_DEFAULT.raceFilter
		),
		[QueryKey.Section]: withDefault(
			StringParam,
			ROUTE_CONTEXT_DEFAULT.sectionAnchor
		),
		[QueryKey.DonorPage]: withDefault(
			StringParam,
			ROUTE_CONTEXT_DEFAULT.donorPage
		)
	});

	const selectedState = query[QueryKey.State] as string,
		election = query[QueryKey.Election] as string,
		seat = query[QueryKey.Seat] as string[],
		congressTab = query[QueryKey.Congress] as number,
		donorTab = query[QueryKey.Donor] as number,
		raceFilter = query[QueryKey.Race] as string[],
		sectionAnchor = query[QueryKey.Section] as string,
		donorPage = query[QueryKey.DonorPage] as string;

	const setSelectedState = (newValue: string | null) =>
		setQuery({ [QueryKey.State]: newValue });
	const setElection = (newValue: string | null) =>
		setQuery({ [QueryKey.Election]: newValue });
	const setSeat = (newValue: string[]) =>
		setQuery({ [QueryKey.Seat]: newValue });
	const setCongressTab = (newValue: number | string | null) =>
		setQuery({ [QueryKey.Congress]: newValue });
	const setDonorTab = (newValue: number | string | null) =>
		setQuery({ [QueryKey.Donor]: newValue });
	const setRaceFilter = (newValue: string[]) =>
		setQuery({ [QueryKey.Race]: newValue });
	const setSectionAnchor = (newValue: string | null) =>
		setQuery({ [QueryKey.Section]: newValue });
	const setDonorPage = (newValue: string | null) =>
		setQuery({ [QueryKey.DonorPage]: newValue });

	return (
		<RouteContext.Provider
			value={{
				selectedState,
				setSelectedState,
				election,
				setElection,
				congressTab,
				setCongressTab,
				donorTab,
				setDonorTab,
				seat,
				setSeat,
				raceFilter,
				setRaceFilter,
				sectionAnchor,
				setSectionAnchor,
				donorPage,
				setDonorPage
			}}
		>
			{children}
		</RouteContext.Provider>
	);
}
